import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { getDashboardAnalytics } from "../apis/dashboard";
import { getWallet } from "../apis/wallet";
import ExploreAIChatbot from "../components/ExploreAIChatbot";
import { useLiveChat } from "../contexts/LiveChatContext";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import { useCampaignList } from "../hooks/useCampaignList";
import { IconBot, IconChat, IconDoc, IconPhone, IconWallet } from "../ui/ping/icons";
import { isSuperAdmin } from "../utils/auth";
import { EMPTY_CAMPAIGN_STATS } from "../utils/campaignList";
import { fmtINR } from "../utils/money";
import { formatQualityRating, qualityTone } from "../utils/phoneHealthLabels";

const EMPTY_FILTERS = {
  status: "",
  campaign_name: "",
  template_id: "",
  whatsapp_account_id: "",
  date_from: "",
  date_to: "",
};

const greetingWord = () => {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
};

const firstNameFromStorage = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const name = String(u.name || "").trim();
    return name.split(/\s+/)[0] || "there";
  } catch {
    return "there";
  }
};

const RANGE_OPTIONS = [
  { days: 7, label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 30, label: "Last 30 days" },
];

const axisLabel = (value, days) => {
  if (!value) return "";
  const raw = String(value);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00+05:30` : raw;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  if (days <= 7) return d.toLocaleDateString("en-IN", { weekday: "short", timeZone: "Asia/Kolkata" });
  return d.toLocaleDateString("en-IN", { month: "short", day: "numeric", timeZone: "Asia/Kolkata" });
};

const campaignChipTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "preparing") return "g";
  if (s === "completed") return "b";
  if (s === "failed" || s === "aborted" || s === "cancelled") return "r";
  return "a";
};

const qualityChipTone = (rating) => {
  const t = qualityTone(rating);
  if (t === "good") return "g";
  if (t === "warn") return "a";
  if (t === "bad") return "r";
  return "n";
};

const PIE_COLORS = {
  sent: "#0064E0",
  received: "#B8D6F8",
  failed: "#E41E3F",
};

const CATEGORY_META = [
  { key: "utility", label: "Utility", color: "#00A884" },
  { key: "marketing", label: "Marketing", color: "#0064E0", also: ["marketing_lite"] },
  { key: "authentication", label: "Authentication", color: "#7E57C2" },
  { key: "service", label: "Session", color: "#B77400" },
];

const DashLoader = ({ height = 200, label = "Loading…" }) => (
  <div className="dash-loader" style={{ minHeight: height }}>
    <div className="dash-spinner" />
    <span>{label}</span>
  </div>
);

const Chevron = () => (
  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16" aria-hidden>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedPhoneAccount, phoneAccounts } = usePhoneAccount();
  const liveChat = useLiveChat();
  const owner = isSuperAdmin();

  const [showExploreChatbot, setShowExploreChatbot] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(owner);
  const [rangeDays, setRangeDays] = useState(7);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);

  const { campaigns: recentCampaigns, loading: campaignsLoading } = useCampaignList({
    page: 1,
    perPage: 3,
    filters: EMPTY_FILTERS,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoadingAnalytics(true);
        const response = await getDashboardAnalytics(rangeDays);
        if (!cancelled && response.data.success && response.data.data) {
          setAnalyticsData(response.data.data);
        }
      } catch (error) {
        console.error("Error loading analytics data:", error);
        if (!cancelled) {
          setAnalyticsData({
            statistics: {
              total_messages: 0,
              sent_count: 0,
              failed_count: 0,
              total_incoming_messages: 0,
              success_rate: 0,
            },
            day_wise: [],
            campaigns: { total: 0, active: 0, completed: 0 },
            broadcast: { total_sent: 0 },
            engagement: { unique_users: 0, total_replies: 0, avg_score: 0 },
            categories: {},
          });
        }
      } finally {
        if (!cancelled) setIsLoadingAnalytics(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [rangeDays]);

  useEffect(() => {
    const close = (e) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target)) setRangeOpen(false);
    };
    if (rangeOpen) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [rangeOpen]);

  useEffect(() => {
    if (!owner) return undefined;
    let cancelled = false;
    setWalletLoading(true);
    getWallet()
      .then((res) => {
        if (!cancelled && res.data?.success) setWallet(res.data.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setWalletLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [owner]);

  const phoneName =
    selectedPhoneAccount?.phoneName ||
    selectedPhoneAccount?.wabaName ||
    "your number";

  const stats = analyticsData?.statistics || {};
  const failedCount = Number(stats.failed_count || 0);
  const successRate = Number(stats.success_rate || 0);

  const kpis = [
    { k: "Total messages", v: Number(stats.total_messages || 0).toLocaleString() },
    { k: "Sent", v: Number(stats.sent_count || 0).toLocaleString() },
    { k: "Received", v: Number(stats.total_incoming_messages || 0).toLocaleString() },
    {
      k: "Failed",
      v: failedCount.toLocaleString(),
      danger: failedCount > 0,
    },
    { k: "Success rate", v: `${successRate.toFixed(1)}%` },
  ];

  const volumeData = useMemo(() => {
    const days = analyticsData?.day_wise || [];
    return days.map((d) => ({
      name: axisLabel(d.date, rangeDays),
      outbound: Number(d.sent_count || 0),
      inbound: Number(d.received_count || 0),
    }));
  }, [analyticsData, rangeDays]);

  const volumeEmpty =
    !isLoadingAnalytics &&
    volumeData.every((d) => d.outbound === 0 && d.inbound === 0);

  const pieData = useMemo(() => {
    const sent = Number(stats.sent_count || 0);
    const received = Number(stats.total_incoming_messages || 0);
    const failed = Number(stats.failed_count || 0);
    return [
      { name: "Sent", value: sent, color: PIE_COLORS.sent },
      { name: "Received", value: received, color: PIE_COLORS.received },
      { name: "Failed", value: failed, color: PIE_COLORS.failed },
    ].filter((s) => s.value > 0);
  }, [stats.sent_count, stats.total_incoming_messages, stats.failed_count]);

  const pieEmpty = !isLoadingAnalytics && pieData.length === 0;

  const categoryRows = useMemo(() => {
    const raw = analyticsData?.categories || {};
    return CATEGORY_META.map((meta) => {
      const keys = [meta.key, ...(meta.also || [])];
      const count = keys.reduce((n, k) => n + Number(raw[k]?.count || 0), 0);
      const spend = keys.reduce((n, k) => n + Number(raw[k]?.spend || 0), 0);
      return { ...meta, count, spend };
    });
  }, [analyticsData]);

  const categoryTotal = categoryRows.reduce((n, r) => n + r.count, 0);
  const categorySpend = categoryRows.reduce((n, r) => n + r.spend, 0);
  const categoryPie = categoryRows.filter((r) => r.count > 0);
  const categoryEmpty = !isLoadingAnalytics && categoryTotal === 0;
  const topCategory = categoryPie.reduce(
    (best, row) => (!best || row.count > best.count ? row : best),
    null
  );
  const topShare = categoryTotal > 0 && topCategory
    ? ((topCategory.count / categoryTotal) * 100).toFixed(0)
    : "0";

  const unread = useMemo(() => {
    const chats = liveChat?.chats || [];
    return chats.reduce((n, c) => n + (Number(c.unread) || 0), 0);
  }, [liveChat?.chats]);

  const walletLow =
    wallet &&
    wallet.status !== "frozen" &&
    Number(wallet.balance) < Number(wallet.low_balance_threshold);

  const goBroadcast = () => navigate("/broadcast");
  const rangeLabel = RANGE_OPTIONS.find((o) => o.days === rangeDays)?.label || "Last 7 days";
  const rangeCaption = rangeLabel.toLowerCase();
  const pieTotal = Number(stats.total_messages || 0);

  return (
    <div className="dash-page">
      <div className="dash-grid">
        <div className="dash-span-12 page-head" style={{ marginBottom: 0 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
              Good {greetingWord()}, {firstNameFromStorage()}
            </h2>
            <p>Here&apos;s how {phoneName} is performing.</p>
          </div>
          <div className="grow" />
          <div className="dash-range" ref={rangeRef}>
            <button
              type="button"
              className="ping-btn ping-btn-ghost"
              onClick={() => setRangeOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={rangeOpen}
            >
              {rangeLabel}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14" aria-hidden>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {rangeOpen && (
              <div className="dash-range-menu" role="listbox">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.days}
                    type="button"
                    className={opt.days === rangeDays ? "on" : ""}
                    onClick={() => {
                      setRangeDays(opt.days);
                      setRangeOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="ping-btn ping-btn-primary" onClick={goBroadcast}>
            New campaign
          </button>
        </div>

        <div className="dash-span-12 dash-kpis">
          {(isLoadingAnalytics ? [1, 2, 3, 4, 5] : kpis).map((stat, i) => (
            <div key={stat.k || i} className={`ping-card ping-stat${isLoadingAnalytics ? " dash-stat-skel" : ""}`}>
              {isLoadingAnalytics ? (
                <>
                  <div className="k" />
                  <div className="v" />
                  <div className="d" />
                </>
              ) : (
                <>
                  <div className="k">{stat.k}</div>
                  <div className="v" style={stat.danger ? { color: "var(--red)" } : undefined}>
                    {stat.v}
                  </div>
                  <div className="d" style={{ color: "var(--text-3)", fontWeight: 500 }}>
                    {rangeCaption}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="dash-span-8">
          <div className="ping-card">
            <h3>Message volume</h3>
            <div className="sub">Outbound billed vs inbound · {rangeCaption}</div>
            {isLoadingAnalytics ? (
              <DashLoader height={200} label="Loading volume…" />
            ) : volumeEmpty ? (
              <div className="dash-empty">
                <span>No messages in this period yet — start a campaign</span>
                <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={goBroadcast}>
                  Create campaign
                </button>
              </div>
            ) : (
              <>
                <div style={{ height: 200, marginTop: 12 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={volumeData} barCategoryGap="18%">
                      <XAxis
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11.5, fill: "#8A8D91" }}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid var(--line)",
                          borderRadius: 8,
                          fontSize: 13,
                        }}
                      />
                      <Bar dataKey="outbound" name="Outbound" fill="#0064E0" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="inbound" name="Inbound" fill="#B8D6F8" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="dash-legend">
                  <span><i style={{ background: "#0064E0" }} />Outbound</span>
                  <span><i style={{ background: "#B8D6F8" }} />Inbound</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="dash-span-4">
          <div className="ping-card">
            <h3>Message mix</h3>
            <div className="sub">Delivered, received and failed · {rangeCaption}</div>
            {isLoadingAnalytics ? (
              <DashLoader height={220} label="Loading mix…" />
            ) : pieEmpty ? (
              <div className="dash-empty" style={{ height: 220 }}>
                <span>No message mix to show yet</span>
              </div>
            ) : (
              <>
                <div className="dash-pie-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={80}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {pieData.map((slice) => (
                          <Cell key={slice.name} fill={slice.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [Number(value).toLocaleString(), name]}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid var(--line)",
                          borderRadius: 8,
                          fontSize: 13,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="dash-pie-center">
                    <b>{pieTotal.toLocaleString()}</b>
                    <span>{successRate.toFixed(1)}% success</span>
                  </div>
                </div>
                <div className="dash-legend" style={{ flexWrap: "wrap" }}>
                  {pieData.map((slice) => (
                    <span key={slice.name}>
                      <i style={{ background: slice.color }} />
                      {slice.name} · {Number(slice.value).toLocaleString()}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="dash-span-12">
          <div className="ping-card">
            <h3>Category mix</h3>
            <div className="sub">Billed delivered messages · {rangeCaption}</div>
            {isLoadingAnalytics ? (
              <DashLoader height={220} label="Loading categories…" />
            ) : categoryEmpty ? (
              <div className="dash-empty" style={{ height: 200 }}>
                <span>No billed messages in this period yet</span>
              </div>
            ) : (
              <div className="dash-cat-split">
                <div>
                  <div className="dash-pie-wrap" style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryPie}
                          dataKey="count"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={80}
                          paddingAngle={2}
                          stroke="none"
                        >
                          {categoryPie.map((slice) => (
                            <Cell key={slice.key} fill={slice.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [Number(value).toLocaleString(), name]}
                          contentStyle={{
                            background: "#fff",
                            border: "1px solid var(--line)",
                            borderRadius: 8,
                            fontSize: 13,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="dash-pie-center">
                      <b>{categoryTotal.toLocaleString()}</b>
                      <span>
                        {topShare}% {topCategory?.label?.toLowerCase() || "mix"}
                      </span>
                    </div>
                  </div>
                  <div className="dash-legend" style={{ flexWrap: "wrap", justifyContent: "center" }}>
                    {categoryPie.map((slice) => (
                      <span key={slice.key}>
                        <i style={{ background: slice.color }} />
                        {slice.label} · {slice.count.toLocaleString()}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  {categoryRows.map((row) => {
                    const share = categoryTotal > 0 ? (row.count / categoryTotal) * 100 : 0;
                    return (
                      <div key={row.key} className="dash-cat-row">
                        <span className="name">{row.label}</span>
                        <span className="bar" aria-hidden>
                          <i style={{ width: `${share}%`, background: row.color }} />
                        </span>
                        <span className="nums">
                          <b>{row.count.toLocaleString()} · {share.toFixed(0)}%</b>
                          {fmtINR(row.spend)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="dash-cat-row" style={{ borderBottom: "none", paddingTop: 4 }}>
                    <span className="name" style={{ color: "var(--text-2)", fontWeight: 500 }}>
                      Spend
                    </span>
                    <span className="bar" />
                    <span className="nums">
                      <b>{fmtINR(categorySpend)}</b>
                      billed in range
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="dash-span-7">
          <div className="ping-card">
            <h3>Recent campaigns</h3>
            {campaignsLoading ? (
              <DashLoader height={140} label="Loading campaigns…" />
            ) : recentCampaigns.length === 0 ? (
              <div className="dash-empty" style={{ height: 140 }}>
                <span>No campaigns yet</span>
                <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={goBroadcast}>
                  Create your first
                </button>
              </div>
            ) : (
              <>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Delivery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCampaigns.slice(0, 3).map((c) => {
                      const st = c.stats || EMPTY_CAMPAIGN_STATS;
                      return (
                        <tr key={c.campaign_id}>
                          <td>
                            <b>{c.campaign_name || "Untitled"}</b>
                          </td>
                          <td>
                            <span className={`ping-chip ${campaignChipTone(c.status)}`}>
                              {String(c.status || "unknown")}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: "var(--text-2)", fontSize: 12.5 }}>
                              {Number(st.sent || 0).toLocaleString()} sent · {Number(st.failed || 0).toLocaleString()} failed
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button type="button" className="dash-foot" onClick={goBroadcast}>
                  View all campaigns →
                </button>
              </>
            )}
          </div>
        </div>

        <div className="dash-span-5">
          <div className="ping-card">
            <h3>Needs attention</h3>
            {walletLoading ? (
              <DashLoader height={140} label="Loading…" />
            ) : (
              <>
            {unread > 0 && (
              <button type="button" className="dash-attn" onClick={() => navigate("/live-chats")}>
                <div className="tile" style={{ background: "var(--blue-soft)", color: "var(--blue)" }}>
                  <IconChat />
                </div>
                <div className="meta">
                  <b>{unread} unread conversation{unread === 1 ? "" : "s"}</b>
                  <span>Open live chat</span>
                </div>
                <Chevron />
              </button>
            )}
            {owner && wallet && (
              <button type="button" className="dash-attn" onClick={() => navigate("/wallet")}>
                <div className="tile" style={{ background: "var(--green-soft)", color: "var(--green-deep)" }}>
                  <IconWallet />
                </div>
                <div className="meta">
                  <b>Wallet {fmtINR(wallet.balance)}</b>
                  <span>{walletLow ? "Recharge to avoid send interruptions" : "Prepaid messaging balance"}</span>
                </div>
                {walletLow && <span className="ping-chip a">Low balance</span>}
                <Chevron />
              </button>
            )}
            {phoneAccounts.map((acc) => (
              <button
                key={acc.phoneId}
                type="button"
                className="dash-attn"
                onClick={() => navigate(acc.phoneId ? `/waba/${acc.phoneId}` : "/waba")}
              >
                <div className="tile" style={{ background: "var(--green-soft)", color: "var(--green-deep)" }}>
                  <IconPhone />
                </div>
                <div className="meta">
                  <b>{acc.phoneName || acc.phoneNumber || "WhatsApp number"}</b>
                  <span>{acc.phoneNumber}</span>
                </div>
                <span className={`ping-chip ${qualityChipTone(acc.qualityRating)}`}>
                  {formatQualityRating(acc.qualityRating)}
                </span>
                <Chevron />
              </button>
            ))}
            {unread === 0 && !(owner && wallet) && phoneAccounts.length === 0 && (
              <p className="sub" style={{ marginTop: 12 }}>Nothing needs attention right now.</p>
            )}
              </>
            )}
          </div>
        </div>

        <div className="dash-span-12">
          <div className="ping-card dash-promo">
            <div className="item">
              <div className="tile"><IconBot /></div>
              <span>Try the AI chatbot — automate replies with a demo conversation</span>
              <button
                type="button"
                className="ping-btn ping-btn-ghost ping-btn-sm"
                onClick={() => setShowExploreChatbot(true)}
              >
                Explore
              </button>
            </div>
            <div className="div" />
            <div className="item">
              <div className="tile"><IconDoc /></div>
              <span>AI Template Explorer — generate templates that get approved faster</span>
              <button
                type="button"
                className="ping-btn ping-btn-ghost ping-btn-sm"
                onClick={() => navigate("/templates/create")}
              >
                Explore
              </button>
            </div>
          </div>
        </div>
      </div>

      <ExploreAIChatbot
        isOpen={showExploreChatbot}
        onClose={() => setShowExploreChatbot(false)}
      />
    </div>
  );
};

export default Dashboard;
